/**
 * Golden15 主链路评测面板（W7）· 接已有 /golden-15/run runner（此前后端能跑、前端无按钮）。
 */
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DEFAULT_EVAL_MERCHANT_ID, type Golden15RunResponse, useRunGolden15 } from "@/lib/api/eval";

const ASPECTS: { key: keyof Golden15RunResponse; label: string }[] = [
	{ key: "intent_passed", label: "意图" },
	{ key: "pb_passed", label: "方案" },
	{ key: "match_mode_passed", label: "命中模式" },
	{ key: "phase_passed", label: "Phase 链路" },
	{ key: "phase5_passed", label: "Phase5 校验" },
];

export function Golden15Panel() {
	const run = useRunGolden15();
	const [result, setResult] = useState<Golden15RunResponse | null>(null);

	function handleRun() {
		setResult(null);
		run.mutate({ merchant_id: DEFAULT_EVAL_MERCHANT_ID }, { onSuccess: (data) => setResult(data) });
	}

	return (
		<Card data-testid="m07-golden15-panel">
			<CardHeader>
				<CardTitle className="text-lg">Golden15 主链路评测</CardTitle>
				<CardDescription>
					15 条金样本逐条跑 L2 意图 → 方案映射 → Phase1-5 → action/outcome，输出逐项命中。
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-3">
				<Button
					type="button"
					onClick={handleRun}
					disabled={run.isPending}
					data-testid="m07-golden15-run"
				>
					{run.isPending ? "运行中…" : "跑 Golden15"}
				</Button>

				{run.isError ? (
					<div className="text-sm text-brand-danger" data-testid="m07-golden15-error">
						运行失败，请重试。
					</div>
				) : null}

				{result ? (
					<div
						className="space-y-3 rounded-md border border-surface-border bg-surface-card p-4"
						data-testid="m07-golden15-result"
					>
						<div className="flex items-baseline gap-3">
							<span className="text-sm text-text-muted">通过</span>
							<span
								className={`text-2xl font-semibold ${result.meets_threshold ? "text-brand-success" : "text-brand-danger"}`}
								data-testid="m07-golden15-passed"
							>
								{result.passed}/{result.total}
							</span>
							<span className="text-sm text-text-muted">
								（{Math.round(result.overall_pass_rate * 100)}% · 阈值{" "}
								{Math.round(result.pass_threshold * 100)}%）
							</span>
							<span
								className={`rounded-full border border-surface-border bg-surface-card px-2 py-0.5 text-xs font-medium ${result.meets_threshold ? "text-brand-success" : "text-brand-danger"}`}
							>
								{result.meets_threshold ? "达标" : "未达标"}
							</span>
						</div>
						<div className="grid gap-2 sm:grid-cols-5">
							{ASPECTS.map((a) => (
								<div
									key={String(a.key)}
									className="rounded-md border border-surface-border bg-surface-background px-3 py-2 text-sm"
								>
									<div className="text-xs text-text-muted">{a.label}</div>
									<div className="mt-1 font-mono text-text-primary">
										{String(result[a.key])}/{result.total}
									</div>
								</div>
							))}
						</div>
					</div>
				) : null}
			</CardContent>
		</Card>
	);
}
